import React, { useState, useEffect } from 'react';
import Menu from './Menu';
import Header from './Header';
import DockBar from './DockBar';
import AppPromo from './AppPromo';
import MouseGlow from '../ui/MouseGlow';
import { PullToRefresh, DebugPanelTabs, ModuleToolbar, ModuleToolbarContext } from '../ui';
import { useTheme } from '../../context/ThemeContext';
import { useDomain } from '../../context/DomainContext';
import { usePreferences } from '../../context/PreferencesContext';
import { isAdmin as checkIsAdmin } from '../../context/AuthContext';
import { useIsMobile } from '../../hooks/useIsMobile';
import { useTranslation } from '../../context/LanguageContext';

const SIDEBAR_ENABLED_KEY = 'cabaza_sidebar_enabled';


/**
 * User layout with vertical navigation sidebar and content area
 * @param {Object} props
 * @param {React.ReactNode} props.children - Page content
 * @param {string} props.currentPage - Current active page
 * @param {Function} props.onNavigate - Navigation handler
 * @param {Function} props.onLogout - Logout handler
 */
const UserLayout = ({ children, currentPage, onNavigate, onLogout }) => {
    // Get theme colors for MouseGlow
    const { currentTheme } = useTheme();

    // Check if user is admin
    const isAdmin = checkIsAdmin();
    const { dockVisible, dockSize, dockPosition } = usePreferences();
    const { t } = useTranslation();
    const isMobile = useIsMobile();


    // Full-screen pages (no padding, no header)
    const isFullScreenPage = currentPage === 'map';

    // Home page smartphone is completely fullscreen (no sidebar, no header)
    const isHomePage = currentPage === 'home';

    // Modules au standard mobile Cabaza (mémoire project_design_mobile_standard) :
    // sur téléphone, ils portent leur propre bande logo + module (ModuleBar), et leur
    // barre du bas (couleur du module, onglets du module) REMPLACE le dock Cabaza, qui ne
    // reste que sur les pages hors module. À compléter module par module.
    const MOBILE_STANDARD_PAGES = {
        expenses: 'emerald', books: 'amber', products: 'blue', moments: 'pink',
        work: 'teal', offers: 'teal', advertisements: 'purple', support: 'emerald', knowledge: 'purple',
        projects: 'purple', meetings: 'pink', carpooling: 'blue',
        timeTracking: 'purple', occupationGroups: 'purple', websites: 'blue',
        contacts: 'purple', places: 'blue', neighborhood: 'pink', messages: 'blue', calendar: 'teal', map: 'amber', events: 'teal'
    };
    const isStandardPage = Object.prototype.hasOwnProperty.call(MOBILE_STANDARD_PAGES, currentPage);
    const [toolbarConfig, setToolbarConfig] = useState(null);

    // Sur une adresse d'application, l'interface se réduit à son module.
    const { isApp } = useDomain();

    // Sidebar enabled state - enabled if items are pinned or if explicitly enabled
    const [isSidebarEnabledBrut, setIsSidebarEnabled] = useState(() => {
        const stored = localStorage.getItem(SIDEBAR_ENABLED_KEY);
        if (stored !== null) return stored === 'true';

        // Default: enable sidebar if there are pinned items
        try {
            const pinnedItems = JSON.parse(localStorage.getItem('cabazaPinnedItems') || '[]');
            return pinnedItems.length > 0;
        } catch (e) {
            return false;
        }
    });

    // La barre latérale porte les groupes et les épinglages. Les uns
    // s'afficheraient vides — leurs modules sont hors périmètre — et les
    // autres viennent du fichier de préférences PARTAGÉ, donc pointent vers
    // des modules masqués ici. Il ne resterait qu'« Accueil ».
    //
    // Le dock reste : il ne contient aucun module métier.
    //
    // Le choix enregistré n'est pas écrasé : il reprend effet dès le retour
    // sur la plateforme.
    const isSidebarEnabled = isApp ? false : isSidebarEnabledBrut;

    // Listen for pinned items changes to auto-enable sidebar
    useEffect(() => {
        const handlePinnedItemsChange = () => {
            // Sur une adresse d'app, un épinglage ne doit pas rouvrir la barre
            // latérale : elle est masquée pour toute la durée de la visite.
            if (isApp) return;
            try {
                const pinnedItems = JSON.parse(localStorage.getItem('cabazaPinnedItems') || '[]');
                if (pinnedItems.length > 0 && !isSidebarEnabled) {
                    setIsSidebarEnabled(true);
                    localStorage.setItem(SIDEBAR_ENABLED_KEY, 'true');
                }
            } catch (e) {
                console.error('Error auto-enabling sidebar:', e);
            }
        };

        window.addEventListener('pinnedItemsChanged', handlePinnedItemsChange);
        return () => window.removeEventListener('pinnedItemsChanged', handlePinnedItemsChange);
    }, [isSidebarEnabled, isApp]);

    // Mobile sidebar state - closed by default on mobile
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    // Toggle mobile menu or navigate to home if sidebar disabled
    const toggleMobileMenu = () => {
        if (!isSidebarEnabled) {
            // If sidebar is disabled, navigate to home
            onNavigate('home');
            return;
        }
        // Only toggle on mobile (sm breakpoint = 640px)
        if (isMobile) {
            setIsMobileMenuOpen(!isMobileMenuOpen);
        }
    };

    // Toggle sidebar enabled state
    const toggleSidebarEnabled = (enabled) => {
        setIsSidebarEnabled(enabled);
        localStorage.setItem(SIDEBAR_ENABLED_KEY, enabled.toString());
    };

    // Close mobile menu when navigating
    const handleNavigate = (page) => {
        setIsMobileMenuOpen(false);
        onNavigate(page);
    };

    // Close menu when resizing to desktop
    useEffect(() => {
        if (!isMobile) {
            setIsMobileMenuOpen(false);
        }
    }, [isMobile]);

    const hideShellHeader = isMobile && isStandardPage;

    // Home page smartphone takes full screen (no PullToRefresh - uses fixed positioning)
    if (isHomePage) {
        return (
            <div className="h-dvh w-screen">
                {children}
            </div>
        );
    }

    return (
        <ModuleToolbarContext.Provider value={{ setConfig: setToolbarConfig }}>
        <div className="flex h-dvh bg-white">
            {/* Mouse glow effect with theme colors */}
            <MouseGlow
                primaryColor={currentTheme?.primaryColor}
                secondaryColor={currentTheme?.secondaryColor}
            />

            {/* Mobile overlay - only if sidebar is enabled */}
            {isSidebarEnabled && isMobileMenuOpen && (
                <div
                    className="sm:hidden fixed inset-0 bg-black/50 z-[9998]"
                    onClick={() => setIsMobileMenuOpen(false)}
                />
            )}

            {/* Vertical Sidebar - Hidden on mobile, overlay when open - only if enabled */}
            {isSidebarEnabled && (
                <div className={`
                    fixed sm:relative z-[9999]
                    transform transition-transform duration-300 ease-in-out
                    ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full sm:translate-x-0'}
                `}>
                    <Menu
                        currentPage={currentPage}
                        onNavigate={handleNavigate}
                        onLogout={onLogout}
                        isSidebarEnabled={isSidebarEnabled}
                        onToggleSidebar={toggleSidebarEnabled}
                    />
                </div>
            )}

            {/* Main Content Area */}
            {isFullScreenPage ? (
                <div className="flex-1 overflow-hidden flex flex-col">
                    {/* Header for fullscreen pages - only on mobile when sidebar is enabled */}
                    {isSidebarEnabled && !hideShellHeader && (
                        <div className="sm:hidden flex-shrink-0 bg-white border-b border-gray-200 px-3 py-2">
                            <Header
                                onToggleMenu={toggleMobileMenu}
                                onLogout={onLogout}
                                onNavigate={onNavigate}
                                showMenuButton={!isMobileMenuOpen}
                                isSidebarEnabled={isSidebarEnabled}
                            />
                        </div>
                    )}
                    {/* Fullscreen content - pass sidebar state for floating controls */}
                    <div className="flex-1 overflow-hidden">
                        {React.cloneElement(children, {
                            isSidebarEnabled,
                            onNavigateHome: () => onNavigate('home'),
                            onNavigateAddContact: () => onNavigate('contacts')
                        })}
                    </div>
                </div>
            ) : (
                <div className={`flex-1 overflow-auto bg-white px-safe ${!isSidebarEnabled ? 'sm:ml-0' : ''}`}>
                    <PullToRefresh>
                        <div className="w-full px-4 pt-safe-2 pb-safe-4 sm:px-6 sm:pt-3 sm:pb-6">
                            {/* Header with hamburger on mobile (absent des modules au standard mobile) */}
                            {!hideShellHeader && (
                                <Header
                                    onToggleMenu={toggleMobileMenu}
                                    onLogout={onLogout}
                                    onNavigate={onNavigate}
                                    showMenuButton={!isMobileMenuOpen || !isSidebarEnabled}
                                    isSidebarEnabled={isSidebarEnabled}
                                />
                            )}

                            {/* Page Content */}
                            {children}

                            {/* Promotion de Cabaza et des autres apps (adresses d'app uniquement) */}
                            <AppPromo />

                            {/* Footer */}
                            <div className={`text-center py-6 mt-4 border-t border-gray-100 ${hideShellHeader ? 'hidden' : ''}`}>
                                <p className="text-xs text-gray-400">
                                    &copy; Cabaza {new Date().getFullYear()} — {t.errorBoundary?.allRightsReserved || 'All rights reserved'}
                                </p>
                            </div>
                        </div>
                    </PullToRefresh>
                </div>
            )}

            {/* Barre du bas : celle du module sur téléphone dans un module, sinon le dock Cabaza */}
            {isMobile && isStandardPage ? (
                <ModuleToolbar
                    config={toolbarConfig}
                    color={MOBILE_STANDARD_PAGES[currentPage]}
                    onModules={isApp ? null : () => onNavigate('modules')}
                />
            ) : (
                dockVisible !== false && (
                    <DockBar currentPage={currentPage} onNavigate={onNavigate} size={dockSize} dockPosition={dockPosition} isSidebarEnabled={isSidebarEnabled} />
                )
            )}

            {/* Debug Panel (for admins only - handles its own visibility) */}
            {isAdmin && <DebugPanelTabs />}
        </div>
        </ModuleToolbarContext.Provider>
    );
};

export default UserLayout;
